import {BillableEvent} from '../types';
import {
    PlatformPropertyCard,
    PlatformPropertyCardCopyButtonDecorator,
    PlatformPropertyCardProperty
} from "@flexinet/ui-components";
import {Code, Stack, Text, Title} from "@mantine/core";
import {StatusBadge} from "./StatusBadge.tsx";
import {prettyPrintAwsJson} from "../utils/parseAwsJson.ts";

const JsonBlock = ({title, value}: { title: string, value: string }) => (
    <Stack gap={'xs'}>
        <Title order={5}>{title}</Title>
        {value
            ? <Code block style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{value}</Code>
            : <Text c={'dimmed'} size={'sm'}>—</Text>}
    </Stack>
);

export const Profile = ({record}: { record: BillableEvent }) => {

    const identityProperties = [
        {
            label: 'ID',
            value: record.id as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'State',
            value: <StatusBadge status={record.state}/>,
            span: 12,
        },
        {
            label: 'Channel',
            value: (record.channel as string) ?? '—',
            span: 12,
        },
        {
            label: 'Customer ID',
            value: record.customerId as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'Meter ID',
            value: record.meterId as string,
            span: 12,
        },
        {
            label: 'Metering Record ID',
            value: record.meteringRecordId as string,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: 'Meter Assignment ID',
            value: (record.meterAssignmentId as string) ?? '—',
            span: 12,
        },
        {
            label: 'Idempotency Key',
            value: (record.idempotencyKey as string) ?? '—',
            span: 12,
        },
        {
            label: 'Record Type',
            value: record.recordType as string,
            span: 12,
        },
    ] as PlatformPropertyCardProperty[];

    const emissionProperties = [
        {
            label: 'Attempts',
            value: typeof record.attempts === 'number' ? String(record.attempts) : '—',
            span: 12,
        },
        {
            label: 'Reason',
            value: (record.reason as string) ?? '—',
            span: 12,
        },
        {
            label: 'State Created',
            value: (record.stateCreated as string) ?? '—',
            span: 12,
        },
        {
            label: 'Last Attempt At',
            value: record.lastAttemptAt as string,
            formatAs: 'Timestamp',
            span: 12,
        },
        {
            label: 'Emitted At',
            value: record.emittedAt as string,
            formatAs: 'Timestamp',
            span: 12,
        },
        {
            label: 'Created',
            value: record.created as string,
            formatAs: 'Timestamp',
            span: 12,
        },
        {
            label: 'Last Updated',
            value: record.updated as string,
            formatAs: 'Timestamp',
            span: 12,
        },
    ] as PlatformPropertyCardProperty[];

    return (
        <Stack gap={'lg'}>
            <PlatformPropertyCard properties={identityProperties}/>
            <PlatformPropertyCard properties={emissionProperties}/>

            {/* channelResponse is the vendor's actual reply (e.g. the Stripe
                billing.meter_event) — the most important payload to inspect. */}
            <JsonBlock title={'Channel Response (vendor reply)'} value={prettyPrintAwsJson(record.channelResponse)}/>
            <JsonBlock title={'Channel Payload (request sent)'} value={prettyPrintAwsJson(record.channelPayload)}/>
        </Stack>
    );
};
